import { useEffect, useState } from "react";
import type { Expense, Wallet } from "@/types";
import { formatCOP } from "@/utils/format";

const ORDER: Wallet[] = ["Bancolombia", "Daviplata", "Nequi", "Caja Social", "Efectivo"];

interface WalletBalancesProps {
  balances: Record<Wallet, number>;
  expenses: Expense[];
  onSave: (wallet: Wallet, value: number) => void;
}

// Saldo inicial por billetera / cuenta y su saldo actual (inicial - gastos).
const WalletBalances = ({ balances, expenses, onSave }: WalletBalancesProps) => {
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    setDraft({});
  }, [balances]);

  const valueOf = (w: Wallet) => draft[w] ?? String(balances[w] ?? 0);

  const commit = (w: Wallet) => {
    const raw = draft[w];
    if (raw === undefined) return;
    const parsed = Number(raw.replace(/[^\d.-]/g, "")) || 0;
    setDraft((prev) => {
      const copy = { ...prev };
      delete copy[w];
      return copy;
    });
    onSave(w, parsed);
  };

  const spentOf = (w: Wallet) =>
    expenses
      .filter((e) => e.wallet === w)
      .reduce((a, e) => a + (e.paidAmount ?? e.amount), 0);

  const totalInitial = ORDER.reduce((a, w) => a + (balances[w] ?? 0), 0);
  const totalSpent = expenses.reduce((a, e) => a + (e.paidAmount ?? e.amount), 0);

  return (
    <div className="glass-panel rounded-lg">
      <div className="px-5 py-4 border-b border-accent-500/15 flex items-center gap-2">
        <span className="w-8 h-8 rounded-md bg-secondary-500/25 text-secondary-200 flex items-center justify-center">
          <i className="ri-safe-2-line" />
        </span>
        <div>
          <h3 className="editable-title text-base font-bold">Saldos por billetera</h3>
          <p className="text-[11px] text-foreground-500">
            Saldo inicial manual · total {formatCOP(totalInitial)}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-background-300/30">
              <th className="text-left font-semibold px-5 py-2">Billetera</th>
              <th className="text-right font-semibold px-3 py-2">Saldo inicial</th>
              <th className="text-right font-semibold px-3 py-2">Gastos</th>
              <th className="text-right font-semibold px-5 py-2">Saldo actual</th>
            </tr>
          </thead>
          <tbody>
            {ORDER.map((w) => {
              const spent = spentOf(w);
              const current = (balances[w] ?? 0) - spent;
              return (
                <tr key={w} className="border-b border-background-300/20">
                  <td className="px-5 py-2 text-foreground-800 font-medium whitespace-nowrap">
                    <i className="ri-wallet-3-line text-foreground-500 mr-2" />
                    {w}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      value={valueOf(w)}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [w]: e.target.value }))}
                      onBlur={() => commit(w)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      }}
                      className="w-28 bg-background-100/70 border border-background-300/50 rounded-md px-2 py-1 text-sm text-right text-foreground-950 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
                      aria-label={`Saldo inicial ${w}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right text-foreground-600 whitespace-nowrap">
                    {formatCOP(spent)}
                  </td>
                  <td
                    className={`px-5 py-2 text-right font-bold whitespace-nowrap ${
                      current < 0 ? "text-primary-200" : "text-foreground-950"
                    }`}
                  >
                    {formatCOP(current)}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-background-200/30">
              <td className="px-5 py-2 font-semibold text-foreground-900">Total</td>
              <td className="px-3 py-2 text-right font-bold text-foreground-950 whitespace-nowrap">
                {formatCOP(totalInitial)}
              </td>
              <td className="px-3 py-2 text-right font-bold text-foreground-800 whitespace-nowrap">
                {formatCOP(totalSpent)}
              </td>
              <td className="px-5 py-2 text-right font-bold text-accent-200 whitespace-nowrap">
                {formatCOP(totalInitial - totalSpent)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="px-5 py-3 text-[10px] text-foreground-500">
        Edita el saldo inicial de cada cuenta y se guarda solo. El saldo actual descuenta los gastos
        registrados.
      </p>
    </div>
  );
};

export default WalletBalances;